/**
 * Meta Pixel Service - Multi-pixel aware.
 * Supports several pixel IDs side-by-side via fbq('trackSingle', pixelId, ...).
 * Generates an eventID for every event so the server-side Conversion API can
 * deduplicate matching pairs.
 */

function generateEventId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function ensureFbqStub() {
  if (window.fbq) return;
  const f = function () {
    f.callMethod ? f.callMethod.apply(f, arguments) : f.queue.push(arguments);
  };
  window.fbq = f;
  f.push = f;
  f.loaded = true;
  f.version = '2.0';
  f.queue = [];
  if (!window._fbq) window._fbq = f;
}

function loadFbScript() {
  if (document.getElementById('meta-pixel-script')) return;
  const script = document.createElement('script');
  script.id = 'meta-pixel-script';
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
}

class MetaPixelService {
  constructor() {
    this.pixelIds = new Set();
    this.isEnabled = true;
  }

  /**
   * Register a pixel id. Calls fbq('init', pixelId) once per id and fires PageView
   * on that single pixel. Loads the fbevents.js script lazily.
   */
  registerPixel(pixelId, { trackPageView = true } = {}) {
    if (!pixelId) return;
    if (this.pixelIds.has(pixelId)) return;

    ensureFbqStub();
    loadFbScript();

    window.fbq('init', pixelId);
    this.pixelIds.add(pixelId);
    if (trackPageView) {
      window.fbq('trackSingle', pixelId, 'PageView');
    }
    console.log('[META PIXEL] registered:', pixelId);
  }

  setEnabled(enabled) {
    this.isEnabled = !!enabled;
  }

  _trackOn(pixelId, eventName, data, eventId) {
    if (!pixelId || !this.isEnabled || !window.fbq) return null;
    const id = eventId || generateEventId();
    window.fbq('trackSingle', pixelId, eventName, data, { eventID: id });
    return id;
  }

  trackPageView(pixelId) {
    return this._trackOn(pixelId, 'PageView', {}, undefined);
  }

  trackViewContent(pixelId, product) {
    if (!product || !product.id) return null;
    return this._trackOn(pixelId, 'ViewContent', {
      content_name: product.name || 'Produit',
      content_ids: [`birk_v2_${product.id}`],
      content_type: 'product',
      value: product.price || 0,
      currency: 'DZD',
    });
  }

  trackInitiateCheckout(pixelId, items, value) {
    if (!items || items.length === 0) return null;
    return this._trackOn(pixelId, 'InitiateCheckout', {
      content_ids: items.map((item) => `birk_v2_${item.id || item.productId}`),
      content_type: 'product_group',
      value: value || 0,
      currency: 'DZD',
      num_items: items.length,
    });
  }

  /**
   * Returns the eventId used so the caller can pass it to the backend for CAPI dedup.
   */
  trackPurchase(pixelId, orderData) {
    if (!orderData || !orderData.items || orderData.items.length === 0) return null;
    const eventId = orderData.eventId || generateEventId();
    this._trackOn(
      pixelId,
      'Purchase',
      {
        content_ids: orderData.items.map((item) => `birk_v2_${item.id || item.productId}`),
        content_type: 'product_group',
        value: orderData.total || orderData.subtotal || 0,
        currency: 'DZD',
        num_items: orderData.items.length,
        order_id: orderData.orderNumber || orderData.orderId,
      },
      eventId
    );
    return eventId;
  }

  generateEventId() {
    return generateEventId();
  }

  getStatus() {
    return {
      pixelIds: Array.from(this.pixelIds),
      isEnabled: this.isEnabled,
      isLoaded: !!window.fbq,
    };
  }
}

export default new MetaPixelService();
